import React from "react";
import { useDispatch } from "react-redux";
import { useConfig } from "../../../hooks/useConfig";
import {
  clearAddress,
  setAddress,
  setAddressLoading,
} from "../../../store/address";
import { setDistanceToPlacementById } from "../../../store/distance";
import { setSortType, SortTypeEnum } from "../../../store/sort";

interface GMapAddressProps {
  map: google.maps.Map | undefined;
  placeId: string | undefined;
}

export const GMapPlaceAddress = ({ map, placeId }: GMapAddressProps): null => {
  const dispatch = useDispatch();
  const config = useConfig();

  React.useEffect(() => {
    if (map && !placeId) {
      // Clearing the address must also drop the derived distances; otherwise a
      // stale value under SAME_ADDRESS_MAX_MILES would render "At this address"
      // to a user who no longer has an address set.
      dispatch(clearAddress());
      dispatch(setDistanceToPlacementById({}));
    }
    if (!map || !placeId) return;

    const placeService = new google.maps.places.PlacesService(map);
    const geocoder = new google.maps.Geocoder();
    dispatch(setAddressLoading());

    placeService.getDetails(
      {
        placeId: placeId.toString(),
        fields: ["place_id", "formatted_address"],
      },
      (place, status) => {
        if (
          status !== google.maps.places.PlacesServiceStatus.OK ||
          !place?.place_id ||
          !place?.formatted_address
        ) {
          console.error("Could not get place details for place ID:", placeId);
        } else {
          geocoder.geocode(
            { address: place.formatted_address },
            (results, geocodeStatus) => {
              if (
                geocodeStatus !== google.maps.GeocoderStatus.OK ||
                !results ||
                results.length === 0 ||
                !results[0]
              ) {
                console.error(
                  "Could not geocode address:",
                  place.formatted_address
                );
              } else {
                const result = results[0];
                const location = result.geometry.location;

                dispatch(
                  setAddress({
                    googlePlaceId: place.place_id!,
                    formattedText: place.formatted_address!,
                    lat: location.lat(),
                    lng: location.lng(),
                  })
                );

                map.panTo({ lat: location.lat(), lng: location.lng() });
                map.setZoom(config?.map.addressZoomLevel ?? 14);

                const nextSortType = config?.boundary?.sort
                  ? SortTypeEnum.BOUNDARY
                  : SortTypeEnum.DISTANCE;

                dispatch(setSortType(nextSortType));
              }
            }
          );
        }
      }
    );
  }, [
    dispatch,
    map,
    placeId,
    config?.boundary?.sort,
    config?.map.addressZoomLevel,
  ]);

  return null;
};
